import { useEffect } from "react";

import { observer } from "mobx-react-lite";
import { useFormatter, useTranslations } from "next-intl";

import * as Dialog from "@/components/dialog/DialogLayout";
import { FormButton } from "@/components/form/FormButton";
import { FormFieldError } from "@/components/form/FormField";
import { Icon } from "@/components/icons/Icon";
import { Spinner } from "@/components/spinner/Spinner";
import { useStore } from "@/store";
import { formatDollars } from "@/utils/formatDollars";
import { twMerge } from "@/utils/twMerge";

import type { Subscription, SubscriptionPlan } from "@/api";
import type { TeamModel } from "@/models/TeamModel";
import type { TeamBillingStore } from "@/store/team/TeamBillingStore";
import type { ReactNode } from "react";

function BillingSection({
  title,
  action,
  children,
}: {
  title: string;
  action: { label: string; onClick: () => void; isBusy?: boolean };
  children: ReactNode;
}) {
  return (
    <section aria-label={title} className="flex flex-col mb-5">
      <Dialog.Subtitle title={title} className="mb-2.5" />
      <div
        className={twMerge(
          "flex items-center gap-2.5 min-h-12 px-2.5 py-2 rounded-8 bg-block-bg shadow-block",
          "text-[15px] leading-[20px] tracking-tight",
        )}
      >
        {children}
        <FormButton
          variant="accent"
          size="sm"
          className="shrink-0 h-9 px-3 rounded-8 text-size-14 tracking-tight"
          isBusy={action.isBusy}
          onClick={action.onClick}
        >
          {action.label}
        </FormButton>
      </div>
    </section>
  );
}

function PlanSummary({
  name,
  caption,
  details,
}: {
  name: string;
  caption?: string;
  details: string;
}) {
  return (
    <div className="flex-1 min-w-0">
      <span className="font-semibold">{name}</span>
      {caption && <span className="text-gray-500">{` · ${caption}`}</span>}
      <div>{details}</div>
    </div>
  );
}

const FreePlan = observer(function FreePlan({ team }: { team: TeamModel }) {
  const t = useTranslations();
  const { dialog } = useStore();

  return (
    <BillingSection
      title={t("teamDialog.billing.currentPlan")}
      action={{
        label: t("teamDialog.billing.upgrade"),
        onClick: () => dialog.upgradePlanDialog.open(team),
      }}
    >
      <PlanSummary
        name={t("upgradePlanDialog.plans.free.name")}
        caption={team.personal ? t("upgradePlanDialog.plans.free.description") : undefined}
        details={t(
          team.personal
            ? "upgradePlanDialog.plans.free.features.credits"
            : "teamDialog.billing.freeTeamCredits",
        )}
      />
    </BillingSection>
  );
});

const PaidPlan = observer(function PaidPlan({
  team,
  subscription,
  plan,
}: {
  team: TeamModel;
  subscription: Subscription;
  plan: SubscriptionPlan;
}) {
  const t = useTranslations();
  const format = useFormatter();
  const { dialog } = useStore();

  const periodEnd = subscription.endsAt ?? subscription.currentPeriodEnd;
  const caption = [
    t("teamDialog.billing.monthlyBilling"),
    periodEnd &&
      t(subscription.endsAt ? "teamDialog.billing.endsOn" : "teamDialog.billing.renews", {
        date: format.dateTime(new Date(periodEnd), { month: "short", day: "numeric" }),
      }),
  ]
    .filter(Boolean)
    .join(" · ");

  const price = formatDollars(plan.priceCents);
  const details =
    plan.kind === "team"
      ? t("teamDialog.billing.seatsFormula", {
          count: team.membersCount,
          price,
          total: formatDollars(plan.priceCents * team.membersCount),
        })
      : t("teamDialog.billing.pricePerMonth", { price });

  return (
    <BillingSection
      title={t("teamDialog.billing.currentPlan")}
      action={{
        label: t("teamDialog.billing.changePlan"),
        onClick: () => dialog.upgradePlanDialog.open(team),
      }}
    >
      <PlanSummary name={plan.name} caption={caption} details={details} />
    </BillingSection>
  );
});

const Payment = observer(function Payment({ billing }: { billing: TeamBillingStore }) {
  const t = useTranslations();

  return (
    <BillingSection
      title={t("teamDialog.billing.payment")}
      action={{
        label: t("teamDialog.billing.manage"),
        onClick: () => billing.openBillingPortal(),
        isBusy: billing.isOpeningBillingPortal,
      }}
    >
      <Icon name="Stripe" className="w-[24px] h-[24px] shrink-0" />
      <div className="flex-1 min-w-0">{t("teamDialog.billing.stripe")}</div>
    </BillingSection>
  );
});

export const PlanBilling = observer(function PlanBilling({
  billing,
}: {
  billing: TeamBillingStore;
}) {
  useEffect(() => {
    billing.load();
  }, [billing]);

  const { team, subscription } = billing;
  const plan = subscription?.subscriptionPlan;

  return (
    <div className="flex flex-col">
      {billing.error && <FormFieldError message={billing.error} className="mb-2.5" />}
      {billing.status === "loading" && (
        <div className="flex items-center justify-center p-10">
          <Spinner />
        </div>
      )}
      {billing.status === "ready" &&
        (subscription && plan ? (
          <>
            <PaidPlan team={team} subscription={subscription} plan={plan} />
            <Payment billing={billing} />
          </>
        ) : (
          <FreePlan team={team} />
        ))}
    </div>
  );
});
